import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import meetingsAPI from '../../services/meetingsApi';
import { authToken } from '../../services/authToken';

// pdf.js worker, served from our own bundle rather than a third-party CDN. The
// board-materials reading path must not depend on cdnjs being reachable, and a
// CDN script URL would block adopting a Content-Security-Policy. The file is
// copied out of pdfjs-dist into public/ by scripts/copy-pdf-worker.js, so the
// version can never drift from the installed library.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

// Authenticated react-pdf file object. The download requires a Bearer header,
// which the global fetch interceptor also supplies; we pass it explicitly so
// react-pdf's own fetch is authorized.
//
// react-pdf compares the `file` prop by reference (===) to decide whether to
// refetch the PDF, so this MUST be memoized by the caller. Building it inline in
// render creates a new object every render, which makes react-pdf reload the
// document on any re-render (e.g. an SSE live.state replay after a heartbeat
// reconnect) — the periodic "slides reload every couple of minutes" bug.
export const authedFile = (fileId: string) => {
  const token = authToken.get();
  return {
    url: meetingsAPI.getFileDownloadUrl(fileId),
    httpHeaders: token ? { Authorization: `Bearer ${token}` } : undefined,
  };
};

export interface DocumentViewerProps {
  /** The material to render. Null renders nothing but the placeholder. */
  fileId: string | null;
  /** 1-based page to display. */
  page: number;
  /** Reports the document's page count once react-pdf has read it. */
  onNumPages?: (fileId: string, numPages: number) => void;
  /** Shown in place of the canvas when there is no file to render. */
  placeholder?: React.ReactNode;
  /** Wrapper class, so the live stage and the prep reader can be styled apart. */
  className?: string;
  /** Class for the loading/error blocks, so each surface keeps its own styling. */
  placeholderClassName?: string;
  /** Copy for the loading and failure states, which differ by surface. */
  loadingLabel?: string;
  errorLabel?: string;
  /** Largest rendered width in px. The page is fitted to the container below this. */
  maxWidth?: number;
}

/**
 * The PDF rendering core, shared by the live slide stage and the meeting-prep
 * material reader so both stay behaviourally identical and share fixes.
 *
 * Four behaviours here are load-bearing bug fixes, not incidental:
 * the memoized `file` object (periodic reload), the `React.memo` wrapper
 * (canvas flicker on parent re-render), `ResizeObserver` width fitting (blank
 * canvas at zero width), and retry-by-remount via `reloadNonce`.
 *
 * The pdf.js text layer stays off for performance, which is why comments anchor
 * to a page rather than to a text range.
 */
const DocumentViewerComponent: React.FC<DocumentViewerProps> = ({
  fileId,
  page,
  onNumPages,
  placeholder,
  className,
  placeholderClassName = 'document-viewer-placeholder',
  loadingLabel = 'Loading document…',
  errorLabel = 'Failed to load document.',
  maxWidth = 960,
}) => {
  const [loadError, setLoadError] = useState<string | null>(null);
  // Bumped by Retry to force the react-pdf Document to remount and refetch.
  const [reloadNonce, setReloadNonce] = useState(0);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [pageWidth, setPageWidth] = useState<number>(720);

  useEffect(() => {
    setLoadError(null);
    setReloadNonce(0);
  }, [fileId]);

  const retry = () => {
    setLoadError(null);
    setReloadNonce((n) => n + 1);
  };

  // Fit the page to the canvas viewport's actual content box, reacting to any
  // layout change (grid reflow, panel resize, tab switch) via ResizeObserver —
  // not just window.resize, which missed element-level changes and could leave a
  // stale/zero width that rendered a blank (black) canvas. A zero/negative
  // measurement is ignored so the page never collapses to nothing.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const update = () => {
      const available = el.clientWidth - 32; // horizontal padding (16px each side)
      if (available > 0) setPageWidth(Math.min(available, maxWidth));
    };
    update();

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [maxWidth]);

  // Keyed on fileId + reloadNonce: only a real file change or an explicit retry
  // should produce a new object and therefore a refetch.
  const fileObj = useMemo(
    () => (fileId ? authedFile(fileId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fileId, reloadNonce]
  );

  // The `onNumPages` callback is the one prop a caller can easily pass unstably
  // (an inline arrow), so it is held in a ref rather than read during render.
  // The rendered subtree below then depends only on values that genuinely change
  // what is on screen, and a parent re-render cannot repaint the canvas.
  const onNumPagesRef = useRef(onNumPages);
  useEffect(() => {
    onNumPagesRef.current = onNumPages;
  }, [onNumPages]);

  const pdf = useMemo(() => {
    if (!fileId || !fileObj) return null;
    return (
      <Document
        key={`${fileId}:${reloadNonce}`}
        file={fileObj}
        onLoadSuccess={({ numPages: n }) => onNumPagesRef.current?.(fileId, n)}
        onLoadError={(err) => setLoadError(err.message || errorLabel)}
        loading={<div className={placeholderClassName}>{loadingLabel}</div>}
        error={
          <div className={placeholderClassName}>
            <div>{loadError || errorLabel}</div>
            <button className="meeting-btn meeting-btn-secondary meeting-btn-sm" onClick={retry}>
              Retry
            </button>
          </div>
        }
      >
        <Page
          pageNumber={page}
          width={pageWidth}
          renderAnnotationLayer={false}
          renderTextLayer={false}
        />
      </Document>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId, fileObj, page, pageWidth, reloadNonce, loadError, placeholderClassName, loadingLabel, errorLabel]);

  return (
    <div className={className ?? 'document-viewer-canvas'} ref={canvasRef}>
      {!fileId && placeholder}
      {pdf}
    </div>
  );
};

const DocumentViewer = React.memo(DocumentViewerComponent);

export default DocumentViewer;
